/**
 * Cooperadic-side diagrams: the cooperadic tree, an animated decomposition
 * with explicit residual-dependency annotations, and the COMPOSE ⊣ DECOMPOSE
 * bar/cobar Koszul-duality adjunction picture.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { FONT, GRAY, withAlpha } from "../../../design-system/tokens.js";
import { usePulsePhase } from "../../shared/index.js";
import { ANIMATION_PERIOD_MS, HubNode, Leaf, RootNode } from "./primitives.js";

/**
 * Mirror of {@link OperadicTree}: one root at the top, three labelled outputs
 * at the bottom. Decomposition flows from root to parts and carries
 * dependency annotations — silent extraction that inherits the whole's
 * safety certificate is structurally inexpressible.
 */
export function CooperadicTree({
  accent,
  title = "Cooperad operation Δ ∈ Q(3)",
}: {
  readonly accent: string;
  readonly title?: string;
}): React.JSX.Element {
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        {title}
      </figcaption>
      <svg
        viewBox="0 0 192 160"
        role="img"
        aria-label="Cooperadic tree: one root branches into three outputs"
      >
        <g stroke={accent} strokeWidth={1.5} fill="none">
          <line x1={96} y1={36} x2={96} y2={66} />
          <line x1={96} y1={82} x2={36} y2={120} />
          <line x1={96} y1={82} x2={96} y2={120} />
          <line x1={96} y1={82} x2={156} y2={120} />
        </g>
        <RootNode cx={96} cy={26} label="whole composite" accent={accent} />
        <HubNode cx={96} cy={74} label="Δ" accent={accent} />
        <Leaf spec={{ id: "out-1", x: 36, y: 130, label: "p₁" }} accent={accent} />
        <Leaf spec={{ id: "out-2", x: 96, y: 130, label: "p₂" }} accent={accent} />
        <Leaf spec={{ id: "out-3", x: 156, y: 130, label: "p₃" }} accent={accent} />
        <text x={6} y={150} fontSize="9" fontFamily={FONT.mono} fill={GRAY[600]}>
          parts + dependency tags
        </text>
      </svg>
    </figure>
  );
}

/**
 * Cooperadic decomposition with explicit dependency annotations: each
 * extracted part is tagged with the residual context it needs from its
 * siblings. The structural reason "safe composition ≠ safe extraction"
 * (the DECOMPOSE Compositionality Principle).
 */
export function CooperadicDecompositionAnimation({
  accent,
}: {
  readonly accent: string;
}): React.JSX.Element {
  const phase = usePulsePhase(3, ANIMATION_PERIOD_MS);
  const showSplit = phase >= 1;
  const showTags = phase >= 2;
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Decomposition: Δ(w) = (p₁, p₂, p₃; deps)
      </figcaption>
      <svg
        viewBox="0 0 240 180"
        role="img"
        aria-label="Cooperadic decomposition: a composite splits into parts tagged with explicit dependencies"
      >
        <g stroke={accent} strokeWidth={1.5} fill="none">
          <line x1={120} y1={36} x2={120} y2={66} />
          <g style={{ transition: "opacity 320ms ease", opacity: showSplit ? 1 : 0.25 }}>
            <line x1={120} y1={82} x2={56} y2={128} />
            <line x1={120} y1={82} x2={120} y2={128} />
            <line x1={120} y1={82} x2={184} y2={128} />
          </g>
        </g>
        <RootNode cx={120} cy={26} label="w : whole" accent={accent} />
        <HubNode cx={120} cy={74} label="Δ" accent={accent} />
        <g style={{ transition: "opacity 320ms ease", opacity: showSplit ? 1 : 0.3 }}>
          <Leaf spec={{ id: "p-1", x: 56, y: 138, label: "p₁" }} accent={accent} />
          <Leaf spec={{ id: "p-2", x: 120, y: 138, label: "p₂" }} accent={accent} />
          <Leaf spec={{ id: "p-3", x: 184, y: 138, label: "p₃" }} accent={accent} />
        </g>
        <g style={{ transition: "opacity 320ms ease", opacity: showTags ? 1 : 0 }}>
          <text
            x={56}
            y={166}
            textAnchor="middle"
            fontSize="9"
            fontFamily={FONT.mono}
            fill={GRAY[700]}
          >
            needs p₂
          </text>
          <text
            x={120}
            y={166}
            textAnchor="middle"
            fontSize="9"
            fontFamily={FONT.mono}
            fill={GRAY[700]}
          >
            needs p₁, p₃
          </text>
          <text
            x={184}
            y={166}
            textAnchor="middle"
            fontSize="9"
            fontFamily={FONT.mono}
            fill={GRAY[700]}
          >
            standalone
          </text>
        </g>
      </svg>
    </figure>
  );
}

/**
 * The COMPOSE ⊣ DECOMPOSE adjunction at the heart of the operad ↔ cooperad
 * Koszul duality. The unit η certifies that every operation has a
 * decomposition; the counit ε certifies that every decomposition recomposes.
 *
 * This is the level-2 SDP picture that organizes Chapter De.
 *
 * The operad's unit is an element `id ∈ P(1)`; the cooperad's counit is a
 * morphism `ε : Q(1) → I` (these are formally dual: an element of `P(1)` is
 * a morphism `I → P(1)`).
 */
export function ComposeDecomposeAdjunction({
  composeAccent,
  decomposeAccent,
}: {
  readonly composeAccent: string;
  readonly decomposeAccent: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        COMPOSE ⊣ DECOMPOSE (operad ⊣ cooperad)
      </figcaption>
      <svg
        viewBox="0 0 320 160"
        role="img"
        aria-label="COMPOSE ⊣ DECOMPOSE adjunction: operad on the left, cooperad on the right, unit and counit between them"
      >
        <defs>
          <marker
            id={`${markerId}-c-head`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10z" fill={composeAccent} />
          </marker>
          <marker
            id={`${markerId}-d-head`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10z" fill={decomposeAccent} />
          </marker>
        </defs>
        <rect
          x={20}
          y={36}
          width={108}
          height={88}
          rx={10}
          fill={withAlpha(composeAccent, "1A")}
          stroke={composeAccent}
          strokeWidth={1.5}
        />
        <text
          x={74}
          y={28}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={composeAccent}
          fontWeight={700}
        >
          Operads
        </text>
        <text
          x={74}
          y={70}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          P : multi-in → one-out
        </text>
        <text
          x={74}
          y={88}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          composition γ
        </text>
        <text
          x={74}
          y={106}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          unit id ∈ P(1)
        </text>
        <rect
          x={192}
          y={36}
          width={108}
          height={88}
          rx={10}
          fill={withAlpha(decomposeAccent, "1A")}
          stroke={decomposeAccent}
          strokeWidth={1.5}
        />
        <text
          x={246}
          y={28}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={decomposeAccent}
          fontWeight={700}
        >
          Cooperads
        </text>
        <text
          x={246}
          y={70}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          Q : one-in → multi-out
        </text>
        <text
          x={246}
          y={88}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          decomposition Δ
        </text>
        <text
          x={246}
          y={106}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          counit ε : Q(1) → I
        </text>
        <path
          d="M128 64 L192 64"
          stroke={composeAccent}
          strokeWidth={2}
          fill="none"
          markerEnd={`url(#${markerId}-c-head)`}
        />
        <text
          x={160}
          y={56}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={composeAccent}
        >
          η : Q → cobar(P)
        </text>
        <path
          d="M192 100 L128 100"
          stroke={decomposeAccent}
          strokeWidth={2}
          fill="none"
          markerEnd={`url(#${markerId}-d-head)`}
        />
        <text
          x={160}
          y={120}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={decomposeAccent}
        >
          ε : bar(Q) → P
        </text>
        <text
          x={160}
          y={148}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.body}
          fill={GRAY[600]}
        >
          Bar / cobar Koszul duality (level-2 SDP)
        </text>
      </svg>
    </figure>
  );
}
